import { useState, useEffect, useRef } from 'react';
import { Dumbbell, Check, ChevronDown, ChevronUp, Loader2, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';

interface Exercise {
  id: string;
  type: 'multiple_choice' | 'short_text';
  prompt: string;
  options?: string[];
  lessonTitle: string;
}

interface WorkoutData {
  id: string;
  exercise: Exercise;
  status: 'pending' | 'completed' | 'skipped';
  assignedDate: string;
  response?: string;
  isCorrect?: boolean;
  explanation?: string | null;
  completedAt?: string;
}

interface SubmitResponse {
  success: boolean;
  isCorrect: boolean;
  explanation: string | null;
  streakUpdated: boolean;
  streak: {
    current: number;
    achievedMilestone: number | null;
  };
}

export default function DailyWorkout() {
  const { user } = useAuth();
  const { fetchApi } = useApi();
  const [workout, setWorkout] = useState<WorkoutData | null>(null);
  const [noWorkout, setNoWorkout] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [textResponse, setTextResponse] = useState('');
  const [submitResult, setSubmitResult] = useState<SubmitResponse | null>(null);
  const [expanded, setExpanded] = useState(false);
  const dataLoadedRef = useRef(false);

  useEffect(() => {
    if (dataLoadedRef.current) return;

    if (user) {
      dataLoadedRef.current = true;
      loadWorkout();
    } else {
      setLoading(false);
    }
  }, [user]);

  async function loadWorkout() {
    try {
      const data = await fetchApi<{ workout: WorkoutData | null; message?: string }>('/api/workouts/today');
      if (data.workout) {
        setWorkout(data.workout);
      } else {
        setNoWorkout(true);
      }
    } catch (error) {
      console.error('Failed to load workout:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit() {
    if (!workout) return;

    const response = workout.exercise.type === 'multiple_choice'
      ? selectedOption
      : textResponse;

    if (response === null || response === '') return;

    setSubmitting(true);
    try {
      const result = await fetchApi<SubmitResponse>('/api/workouts/today/submit', {
        method: 'POST',
        body: JSON.stringify({ response })
      });
      setSubmitResult(result);
      setWorkout(prev => prev ? { ...prev, status: 'completed' } : null);
    } catch (error) {
      console.error('Failed to submit workout:', error);
    } finally {
      setSubmitting(false);
    }
  }

  // Not logged in or loading
  if (!user || loading) {
    return null;
  }

  // No exercises available (user hasn't completed any lessons)
  if (noWorkout) {
    return (
      <div className="bg-background-secondary rounded-xl border border-gray-800 p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center">
            <Dumbbell className="w-5 h-5 text-gray-500" />
          </div>
          <h3 className="font-semibold text-white">Daily Workout</h3>
        </div>
        <p className="text-gray-400 text-sm mb-4">
          Complete your first lesson to unlock daily workouts!
        </p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-4 py-2 bg-accent-blue text-slate-900 text-sm rounded-lg hover:bg-blue-500 transition-colors"
        >
          <BookOpen className="w-4 h-4" />
          Browse Tracks
        </Link>
      </div>
    );
  }

  if (!workout) return null;

  // Completed state
  if (workout.status === 'completed' || submitResult) {
    const isCorrect = submitResult?.isCorrect ?? workout.isCorrect;
    const explanation = submitResult?.explanation ?? workout.explanation;

    return (
      <div className="bg-background-secondary rounded-xl border border-gray-800 overflow-hidden">
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full p-5 flex items-center justify-between hover:bg-gray-800/30 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
              <Check className="w-5 h-5 text-green-400" />
            </div>
            <div className="text-left">
              <h3 className="font-semibold text-white">Workout Complete!</h3>
              <p className="text-sm text-gray-400">
                {workout.exercise.type === 'multiple_choice'
                  ? (isCorrect ? 'Correct answer' : 'Incorrect, but you learned!')
                  : 'Response recorded'}
              </p>
            </div>
          </div>
          {expanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>

        {expanded && (
          <div className="px-5 pb-5 border-t border-gray-800 pt-4">
            <p className="text-sm text-gray-500 mb-2">From: {workout.exercise.lessonTitle}</p>
            <p className="text-gray-300 mb-3">{workout.exercise.prompt}</p>
            {explanation && (
              <div className="p-3 bg-gray-800/50 rounded-lg text-sm text-gray-400">
                <span className="text-white font-medium">Explanation: </span>
                {explanation}
              </div>
            )}
          </div>
        )}

        {submitResult?.streakUpdated && (
          <div className="px-5 pb-5">
            <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg text-orange-400 text-sm">
              Streak extended! You're now at {submitResult.streak.current} days.
              {submitResult.streak.achievedMilestone && (
                <span className="block mt-1 font-semibold">
                  Milestone reached: {submitResult.streak.achievedMilestone} days!
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Pending state - show exercise
  const canSubmit = workout.exercise.type === 'multiple_choice'
    ? selectedOption !== null
    : textResponse.trim().length >= 10;

  return (
    <div className="bg-background-secondary rounded-xl border-2 border-accent-blue/50 overflow-hidden">
      {/* Header */}
      <div className="p-5 bg-gradient-to-r from-accent-blue/10 to-accent-purple/10">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 rounded-full bg-accent-blue/20 flex items-center justify-center">
            <Dumbbell className="w-5 h-5 text-accent-blue" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Daily Workout</h3>
            <p className="text-xs text-gray-400">From: {workout.exercise.lessonTitle}</p>
          </div>
        </div>
      </div>

      {/* Exercise */}
      <div className="p-5">
        <p className="text-gray-200 mb-4">{workout.exercise.prompt}</p>

        {workout.exercise.type === 'multiple_choice' && workout.exercise.options && (
          <div className="space-y-2">
            {workout.exercise.options.map((option, index) => (
              <button
                key={index}
                onClick={() => setSelectedOption(index)}
                className={`w-full text-left p-3 rounded-lg border transition-colors ${
                  selectedOption === index
                    ? 'border-accent-blue bg-accent-blue/10 text-white'
                    : 'border-gray-700 bg-gray-800/50 text-gray-300 hover:border-gray-600'
                }`}
              >
                <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full mr-3 text-sm font-medium ${
                  selectedOption === index
                    ? 'bg-accent-blue text-slate-900'
                    : 'bg-gray-700 text-gray-400'
                }`}>
                  {String.fromCharCode(65 + index)}
                </span>
                {option}
              </button>
            ))}
          </div>
        )}

        {workout.exercise.type === 'short_text' && (
          <div>
            <textarea
              value={textResponse}
              onChange={(e) => setTextResponse(e.target.value)}
              placeholder="Your response (at least 10 characters)..."
              className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-accent-blue focus:ring-1 focus:ring-accent-blue outline-none resize-none"
              rows={3}
            />
            <p className="text-xs text-gray-500 mt-1">
              {textResponse.length}/10 characters minimum
            </p>
          </div>
        )}

        <button
          onClick={handleSubmit}
          disabled={!canSubmit || submitting}
          className="w-full mt-4 py-3 px-4 bg-accent-blue hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-slate-900 font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <Check className="w-4 h-4" />
              Submit
            </>
          )}
        </button>
      </div>
    </div>
  );
}
